import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";

// A real hover/focus tooltip (not the browser's native `title`, which has a
// delay, no styling, no keyboard or touch access, and cannot hold a long
// explanation). Shows on mouse hover and on keyboard focus, hides on leave,
// blur or Escape. The bubble is `position: fixed` so a scrolling table
// container never clips it, and it is clamped to the viewport.
const BUBBLE_MAX_WIDTH = 340;
const EDGE_GAP = 8;

// `anchorTo`: place the bubble by the nearest ancestor matching this selector (e.g. a whole list)
// instead of the element itself, so it opens clear of that ancestor.
// `placement`: "below" (default) opens under the anchor; "above" opens over it
// (so it never covers what sits below), or to its side when there is no room
// above; "side" opens to its right, or its left when there is no room, level
// with its top; "left" opens to its left (away from whatever sits to its
// right), or above it when there is no room.
export type TipPlacement = "below" | "above" | "side" | "left";

interface Box { left: number; top: number; right: number; bottom: number }
interface Pos { left: number; top?: number; bottom?: number }

/** Where the bubble goes for an anchor box, given the bubble's measured size
 *  and the viewport. Pure, so the fallbacks are the same wherever it is used. */
export function placeTip(
  placement: TipPlacement, a: Box, w: number, h: number, vw: number, vh: number,
): Pos {
  const clampLeft = (x: number) => Math.max(EDGE_GAP, Math.min(x, vw - w - EDGE_GAP));
  const side = (): Pos => {
    const room = vw - a.right - EDGE_GAP * 2;
    const sideLeft = room >= w ? a.right + 8 : Math.max(EDGE_GAP, a.left - 8 - w);
    return { left: Math.min(sideLeft, vw - w - EDGE_GAP), top: Math.max(EDGE_GAP, Math.min(a.top, vh - h - EDGE_GAP)) };
  };
  const above = (): Pos | null =>
    a.top - 6 - h >= EDGE_GAP ? { left: clampLeft(a.left), bottom: vh - a.top + 6 } : null;
  if (placement === "above") return above() ?? side();
  if (placement === "left") {
    if (a.left - 8 - w >= EDGE_GAP) return { left: a.left - 8 - w, top: Math.max(EDGE_GAP, Math.min(a.top, vh - h - EDGE_GAP)) };
    return above() ?? side();
  }
  if (placement === "side") return side();
  return { left: clampLeft(a.left), top: a.bottom + 6 };
}

export function HoverTip({
  text, children, block = false, className, placement = "below", anchorTo,
}: { text: string; children: ReactNode; block?: boolean; className?: string; placement?: TipPlacement; anchorTo?: string }) {
  const id = useId();
  const anchor = useRef<HTMLSpanElement>(null);
  const bubble = useRef<HTMLSpanElement>(null);
  const [box, setBox] = useState<Box | null>(null);
  const [pos, setPos] = useState<Pos | null>(null);

  function show() {
    const el = anchor.current;
    if (!el) return;
    // `anchorTo`: a CSS selector of an ancestor whose box places the bubble instead of this element's own.
    const r = (anchorTo ? el.closest(anchorTo) : null)?.getBoundingClientRect() ?? el.getBoundingClientRect();
    setPos(null);
    setBox({ left: r.left, top: r.top, right: r.right, bottom: r.bottom });
  }
  function hide() {
    setBox(null);
    setPos(null);
  }

  // The bubble is first drawn out of sight, measured, then put where it fits.
  useLayoutEffect(() => {
    if (!box || !bubble.current) return;
    const w = Math.min(bubble.current.offsetWidth, window.innerWidth - EDGE_GAP * 2);
    setPos(placeTip(placement, box, w, bubble.current.offsetHeight, window.innerWidth, window.innerHeight));
  }, [box, placement]);

  useEffect(() => {
    if (!box) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") hide(); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", hide, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", hide, true);
    };
  }, [box]);

  return (
    <span
      ref={anchor}
      className={`hover-tip-anchor${block ? " hover-tip-block" : ""}${className ? ` ${className}` : ""}`}
      tabIndex={0}
      aria-describedby={box ? id : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {box && (
        <span
          id={id}
          ref={bubble}
          role="tooltip"
          className="hover-tip-bubble"
          style={{
            left: pos?.left ?? 0, top: pos?.top, bottom: pos?.bottom,
            maxWidth: Math.min(BUBBLE_MAX_WIDTH, window.innerWidth - EDGE_GAP * 2),
            visibility: pos ? "visible" : "hidden",
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}
